import { ReactNode, useEffect, useRef, useState } from "react";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

type CardProps = {
  target: string;
  target2: string;
  content: ReactNode;
  emoticon: string;
  emoticon2: string;
  src?: string;
  src2?: string;
};

const Card = ({ target, target2, content, emoticon, src, src2 }: CardProps) => (
  <section
    className="
      h-[466px] md:h-[540px]
      flex flex-col
      px-6 py-[38px] md:p-9
      rounded-[32px]
      border-[3px] border-transparent
      [background-origin:border-box]
      [background-clip:padding-box,border-box]
      bg-[linear-gradient(#fff,#fff),linear-gradient(90deg,#0079FF_0%,#00FF44_56.09%,#FFC400_100%)]
      w-full max-w-[436px]
      max-md:max-w-none shrink-0 snap-start
    "
  >
    <div className="flex-1 flex flex-col justify-start items-start gap-6 md:gap-8">
      <div className="flex flex-col gap-3">
        <span className="font-normal text-[18px] md:text-[28px] text-[#121319]">{target}</span>

        <div className="flex flex-row items-center gap-2">
          <img src={src2} className="relative w-5 md:w-10 h-auto mb-[4px]" />
          <span className="font-bold text-[20px] md:text-[36px] text-[#121319]">{target2}</span>
        </div>
      </div>

      <div className="flex flex-col justify-start items-start w-full gap-3 md:gap-6">
        <img width={21} height={18} src="/assets/images/icon/double_quotes.png" alt="quotes" />
        {content}
        <img
          className="self-end"
          width={21}
          height={18}
          src="/assets/images/icon/double_quotes2.png"
          alt="quotes"
        />
      </div>
    </div>

    {/* 아래 고정 */}
    <img src={src} className="w-10 self-end" />
  </section>
);

/**  모바일에서만: 요소 60% 보이면 아래→위로 등장 */
function useMobileInView<T extends HTMLElement>(
  options: IntersectionObserverInit = {
    threshold: 0.2,
    rootMargin: "0px 0px -20% 0px",
  },
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  const isMobile = useResponsiveMobile(900);
  useEffect(() => {
    if (!isMobile) return;

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);

    io.observe(el);
    return () => io.disconnect();
  }, [options, isMobile]);

  return { ref, inView };
}

export const Section2 = () => {
  const isMobile = useResponsiveMobile(900);
  const row1Ref = useRef<HTMLDivElement | null>(null);
  const row2Ref = useRef<HTMLDivElement | null>(null);

  const [showRow1, setShowRow1] = useState(false);
  const [showRow2, setShowRow2] = useState(false);

  const cards: CardProps[] = [
    {
      target: "업무 효율성을 추구하는",
      target2: "운영 실무 담당자",
      emoticon: "🧑‍💼",
      emoticon2: "😮‍💨",
      content: (
        <span className="font-normal text-[18px] md:text-[20px] text-[#4D4F58]">
          정산 마감 날이면 숫자가 맞는지 <br />
          다시 확인하느라 하루가 사라져요. <br />
          자동화가 없다면, 이 반복은 끝나지{isMobile && <br />} 않아요.
        </span>
      ),
      src: "/assets/images/emoticon/intro_emoticon1.png",
      src2: "/assets/images/emoticon/card1_emoticon.png",
    },
    {
      target: "성과를 만들어야 하는",
      target2: "영업 담당자",
      emoticon: "💼",
      emoticon2: "😔",
      content: (
        <span className="font-normal text-[18px] md:text-[20px] text-[#4D4F58]">
          엑셀과 메신저로 관리하다 보면 <br /> 업무에 한계를 느껴요.
          <br />
          <br />
          보안 사고는 늘 불안하고,
          <br /> 정작 집중해야할 파트너십 강화와 <br /> 성과 분석은 시작도 못하고 있어요
        </span>
      ),
      src: "/assets/images/emoticon/intro_emoticon2.png",
      src2: "/assets/images/emoticon/card2_emoticon.png",
    },
    {
      target: "경영 안정성을 고민하는",
      target2: "대표",
      emoticon: "🏢",
      emoticon2: "🫠",
      content: (
        <span className="font-normal text-[18px] md:text-[20px] text-[#4D4F58]">
          자체 시스템 구축은 <br />
          비용과 기간 모두 부담스럽고, <br />
          <br />
          직접 정산을 수행하자니 <br />
          금융 사고나 법적 리스크 <br />
          관리 체계가 없어 막막합니다
        </span>
      ),
      src: "/assets/images/emoticon/intro_emoticon3.png",
      src2: "/assets/images/emoticon/card3_emoticon.png",
    },
    {
      target: "성장을 고민하는",
      target2: "본부장",
      emoticon: "🤝🏼",
      emoticon2: "🤔",
      content: (
        <span className="font-normal text-[18px] md:text-[20px] text-[#4D4F58]">
          타사보다 정산이 늦어지면 <br />
          가맹점은 바로 이탈해요. <br />
          <br />
          고정된 정산 주기(D+3)를 단축하여
          <br />
          파트너의 자금 유동성을 <br />
          해결할 방법이 시급합니다.
          <br />
        </span>
      ),
      src: "/assets/images/emoticon/intro_emoticon4.png",
      src2: "/assets/images/emoticon/card4_emoticon.png",
    },
  ];

  //  PC( md 이상 ) 행별 등장 애니메이션 (기존 유지)
  useEffect(() => {
    const r1 = row1Ref.current;
    const r2 = row2Ref.current;
    if (!r1 || !r2) return;

    const io1 = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowRow1(true);
          io1.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -35% 0px" },
    );

    const io2 = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowRow2(true);
          io2.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -35% 0px" },
    );

    io1.observe(r1);
    io2.observe(r2);

    return () => {
      io1.disconnect();
      io2.disconnect();
    };
  }, []);

  //  모바일: 제목/캐러셀 영역 등장 애니메이션
  const { ref: mobileTitleRef, inView: mobileTitleInView } = useMobileInView<HTMLSpanElement>({
    threshold: 0.6,
  });

  const { ref: mobileCarouselRef, inView: mobileCarouselInView } = useMobileInView<HTMLDivElement>({
    threshold: 0.1,
    rootMargin: "0px 0px -20% 0px",
  });

  // 공통 애니메이션 클래스
  const enterBase = "transition-all duration-[1000ms] ease-out will-change-transform";
  const enterHidden = "opacity-0 translate-y-8";
  const enterShown = "opacity-100 translate-y-0";

  const rowBase = "flex flex-row gap-8 transition-all duration-700 ease-out";
  const rowHidden = "opacity-0 translate-y-6";
  const rowShown = "opacity-100 translate-y-0";

  return (
    <div className="flex flex-col gap-20 items-center justify-center w-full">
      {/*  모바일에서만 스크롤 등장 */}
      <div className="flex flex-col gap-3">
        <span className="font-normal text-[#0079FF] text-center text-[18px]">Who is it for?</span>
        <span
          ref={mobileTitleRef}
          className={[
            "font-bold text-[28px] md:text-display2_1 text-center",
            enterBase,
            isMobile ? (mobileTitleInView ? enterShown : enterHidden) : "",
          ].join(" ")}
        >
          정산 관리, 매출 확장, {isMobile && <br />} 신규거래처 확보... <br />
          <span className="text-[40px] md:text-display text-[#0079FF]">
            이런 고민 때문에 {isMobile && <br />} 힘드신가요?
          </span>
        </span>
      </div>
      {/*  Mobile: 1,2,3,4 한 섹션 캐러셀 + 스크롤 등장 */}
      <div className="md:hidden w-full px-6 md:px-0">
        <div
          ref={mobileCarouselRef}
          className={[enterBase, mobileCarouselInView ? enterShown : enterHidden].join(" ")}
        >
          <div className="flex flex-col gap-3 items-center">
            {cards.map((c, idx) => (
              <Card key={idx} {...c} />
            ))}
          </div>
        </div>
      </div>

      {/*  PC: 2x2 + IntersectionObserver (기존) */}
      <div className="hidden w-full md:flex flex-col gap-8">
        <div
          ref={row1Ref}
          className={`${rowBase} ${showRow1 ? rowShown : rowHidden} justify-center`}
        >
          <Card {...cards[0]} />
          <Card {...cards[1]} />
        </div>

        <div
          ref={row2Ref}
          className={`${rowBase} ${showRow2 ? rowShown : rowHidden} justify-center`}
        >
          <Card {...cards[2]} />
          <Card {...cards[3]} />
        </div>
      </div>
    </div>
  );
};
